import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { profileService, type ProfileData } from '@/features/profile/services/profile.service'
import { useAuth } from '@/core/auth/hooks/useAuth'
import { useResources } from '@/features/resources/hooks/useResources'
import { useResourceUrl } from '@/features/resources/hooks/useResourceUrl'
import type { Resource } from '@/features/resources/types'
import { formatPersonName } from '@/shared/utils/formatters'
import { isHttpError } from '@/core/api/errors/HttpError'

const VALID_SYMBOLS = /[-@!"#$*%&/=?]/

function Field({ label, value }: { label: string; value: string }) {
  return <label className="flex flex-col gap-1 text-[0.7rem] text-[#4D4C4E]"><span>{label}</span><input value={value} readOnly className="h-10 rounded-md border border-[#1C3042] bg-white px-3 text-xs text-[#1A191A] outline-none" /></label>
}

export function ProfilePage() {
  const { t, i18n } = useTranslation('profile')
  const { logout } = useAuth()
  const navigate = useNavigate()
  const { resources } = useResources('onboarding', 'document')
  const { getUrl } = useResourceUrl()
  const [profile, setProfile] = useState<ProfileData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [password, setPassword] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' })
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordSuccess, setPasswordSuccess] = useState(false)
  const [legalDocument, setLegalDocument] = useState<{ title: string; url: string } | null>(null)

  useEffect(() => {
    void profileService.get().then(setProfile).catch((reason: unknown) => setError(reason instanceof Error ? reason.message : t('loadError'))).finally(() => setLoading(false))
  }, [t])

  const changePassword = async (event: React.FormEvent) => {
    event.preventDefault()
    setPasswordError(null)
    setPasswordSuccess(false)
    setChangingPassword(true)
    try {
      await profileService.changePassword(password, i18n.language.startsWith('en') ? 'en' : 'es')
      setChangingPassword(false)
      setPasswordSuccess(true)
      window.setTimeout(() => void logout(), 2500)
    } catch (reason: unknown) {
      const message = isHttpError(reason) && reason.status === 401
        ? t('password.currentInvalid')
        : t('password.error')
      setPasswordError(message)
      setChangingPassword(false)
    }
  }

  if (loading) return <div className="p-8 text-sm text-text-muted">{t('loading')}</div>
  if (error || !profile) return <div className="p-8 text-sm text-[#b4232f]">{error ?? t('loadError')}</div>
  const birthDate = profile.birthDate ? new Date(profile.birthDate).toLocaleDateString(i18n.language.startsWith('en') ? 'en-US' : 'es-EC') : '-'
  const passwordRules = [
    { valid: password.newPassword.length >= 8 && password.newPassword.length <= 16, label: t('password.rules.minLength') },
    { valid: /\d/.test(password.newPassword), label: t('password.rules.number') },
    { valid: VALID_SYMBOLS.test(password.newPassword), label: t('password.rules.symbol') },
    { valid: /[A-Z]/.test(password.newPassword), label: t('password.rules.uppercase') },
  ]
  const canChangePassword = password.currentPassword.length > 0 && passwordRules.every((rule) => rule.valid) && password.newPassword === password.confirmPassword
  const completedRules = passwordRules.filter((rule) => rule.valid).length
  const passwordsMatch = password.newPassword.length > 0 && password.newPassword === password.confirmPassword
  const passwordsMismatch = password.confirmPassword.length > 0 && !passwordsMatch
  const strengthState = completedRules >= 4 ? 'success' : completedRules >= 2 ? 'warning' : 'danger'
  const findLegalResource = (keywords: string[]) => resources.find((resource: Resource) => keywords.some((keyword) => resource.name.toLowerCase().includes(keyword)))
  const termsResource = findLegalResource(['terminos', 'terms', 'condiciones', 'conditions'])
  const privacyResource = findLegalResource(['proteccion', 'protección', 'privacy', 'datos', 'policy'])

  const openLegalResource = async (resource?: Resource, title?: string) => {
    if (!resource) return
    const url = await getUrl(resource.id)
    if (url) setLegalDocument({ title: title ?? resource.name, url })
  }

  return <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-1 py-2">
    <h1 className="m-0 text-[1.25rem] font-semibold text-[#003d66]">{t('title')}</h1>
    <div className="grid items-start gap-x-11 gap-y-8 lg:grid-cols-[1fr_1fr]">
      <section>
        <h2 className="sr-only">{t('personalData')}</h2>
        <div className="grid content-start gap-3">
        <Field label={t('fields.firstName')} value={formatPersonName(profile.firstName) || '-'} />
        <Field label={t('fields.lastName')} value={formatPersonName(profile.lastName) || '-'} />
        <Field label={t('fields.governmentId')} value={profile.governmentId ?? '-'} />
        <Field label={t('fields.email')} value={profile.email ?? '-'} />
        <Field label={t('fields.mobile')} value={profile.mobilePhone ?? '-'} />
        <Field label={t('fields.birthDate')} value={birthDate} />
        <Field label={t('fields.country')} value={profile.country?.name ?? '-'} />
        </div>
      </section>
      <section>
        <h2 className="m-0 text-sm font-semibold text-[#273d4a]">{t('password.title')}</h2>
        <form onSubmit={changePassword} className="mt-3 flex max-w-[31rem] flex-col gap-3">
          <label className="flex flex-col gap-1.5 text-[0.7rem] text-[#4D4C4E]">
            <span>{t('password.current')}</span>
            <span className="relative flex items-center">
              <input type={showCurrentPassword ? 'text' : 'password'} value={password.currentPassword} onChange={(e) => setPassword({ ...password, currentPassword: e.target.value })} required className="h-10 w-full rounded-md border border-[#1C3042] px-3 pr-10 text-xs text-[#1A191A] outline-none focus:border-[#007acc]" />
              <button type="button" aria-label={showCurrentPassword ? t('password.hide') : t('password.show')} onClick={() => setShowCurrentPassword((current) => !current)} className="absolute right-4 inline-flex cursor-pointer items-center border-none bg-transparent p-0 text-[#606975] focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#007acc]">◉</button>
            </span>
          </label>
          <label className="flex flex-col gap-1.5 text-[0.7rem] text-[#4D4C4E]">
            <span>{t('password.new')}</span>
            <span className="relative flex items-center">
              <input type={showNewPassword ? 'text' : 'password'} maxLength={16} value={password.newPassword} onChange={(e) => setPassword({ ...password, newPassword: e.target.value })} required className="h-10 w-full rounded-md border border-[#1C3042] px-3 pr-10 text-xs text-[#1A191A] outline-none focus:border-[#007acc]" />
              <button type="button" aria-label={showNewPassword ? t('password.hide') : t('password.show')} onClick={() => setShowNewPassword((current) => !current)} className="absolute right-4 inline-flex cursor-pointer items-center border-none bg-transparent p-0 text-[#606975] focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#007acc]">◉</button>
            </span>
          </label>
          <div className="h-2 w-full overflow-hidden rounded-full bg-[#e5edf4]">
            <span className={["block h-full rounded-full transition-[width,background-color] duration-200", strengthState === 'success' ? 'bg-[var(--color-success,#22c55e)]' : strengthState === 'warning' ? 'bg-[var(--color-warning,#f59e0b)]' : 'bg-[#ff384d]'].join(' ')} style={{ width: `${Math.max((completedRules / passwordRules.length) * 100, 4)}%` }} />
          </div>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {passwordRules.map((rule) => (
              <li key={rule.label} className="flex items-center gap-2 text-sm leading-5 text-text-muted">
                <span className={rule.valid ? 'inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#e8f8ef] text-xs text-[var(--color-success,#22c55e)]' : 'inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#eef3f7] text-xs text-text-muted'} aria-hidden="true">{rule.valid ? '✓' : '○'}</span>
                <span>{rule.label}</span>
              </li>
            ))}
          </ul>
          <label className="flex flex-col gap-1.5 text-[0.7rem] text-[#526b7b]">
            <span>{t('password.confirm')}</span>
            <span className="relative flex items-center">
              <input type={showConfirmPassword ? 'text' : 'password'} maxLength={16} value={password.confirmPassword} onChange={(e) => setPassword({ ...password, confirmPassword: e.target.value })} required className="h-10 w-full rounded-md border border-[#1C3042] px-3 pr-10 text-xs text-[#1A191A] outline-none focus:border-[#007acc]" />
              <button type="button" aria-label={showConfirmPassword ? t('password.hide') : t('password.show')} onClick={() => setShowConfirmPassword((current) => !current)} className="absolute right-4 inline-flex cursor-pointer items-center border-none bg-transparent p-0 text-[#606975] focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#007acc]">◉</button>
            </span>
          </label>
          {passwordsMatch ? <p className="m-0 text-center text-sm leading-6 text-[var(--color-success,#22c55e)]">{t('password.rules.match')}</p> : passwordsMismatch ? <p className="m-0 text-center text-sm leading-6 text-[#e02d3c]" role="alert">{t('password.rules.mismatch')}</p> : null}
          {passwordSuccess && <div className="rounded-md border border-[#b7e5cb] bg-[#e8f8ef] px-3 py-3 text-center text-sm leading-5 text-[#168b57]" role="status">{t('password.success')}</div>}
          {passwordError && <p className="m-0 text-xs text-[#b4232f]">{passwordError}</p>}
          <button type="submit" disabled={changingPassword || passwordSuccess || !canChangePassword} className="h-10 rounded-sm bg-[#007acc] text-body-2 font-normal text-white hover:bg-[#0066aa] disabled:cursor-not-allowed disabled:bg-[#d7e0e7] disabled:text-[#8c9aa4]">{changingPassword ? t('password.saving') : t('password.submit')}</button>
        </form>
        <div className="mt-7 flex items-center justify-between gap-4">
          <div>
            <p className="m-0 text-xs font-medium text-[#273d4a]">{t('suspension.profileAction')}</p>
            <p className="mt-1 text-[0.65rem] text-[#526b7b]">{t('suspension.profileHint')}</p>
          </div>
          <button type="button" onClick={() => navigate('/suspender-cuenta')} className="shrink-0 cursor-pointer rounded-sm bg-[#007acc] px-6 py-3 text-body-2 font-normal text-white hover:bg-[#0066aa]">{t('suspension.profileButton')}</button>
        </div>
        <div className="mt-8 border-t border-[#e3eaf0] pt-4">
          <div className="mt-3 flex flex-col gap-2 text-xs">
            <button type="button" disabled={!termsResource} onClick={() => void openLegalResource(termsResource, t('legal.terms'))} className="w-fit cursor-pointer text-left text-[#1A191A] underline disabled:cursor-not-allowed disabled:opacity-50">{t('legal.terms')}</button>
            <button type="button" disabled={!privacyResource} onClick={() => void openLegalResource(privacyResource, t('legal.privacy'))} className="w-fit cursor-pointer text-left text-[#1A191A] underline disabled:cursor-not-allowed disabled:opacity-50">{t('legal.privacy')}</button>
          </div>
        </div>
      </section>
    </div>
    {legalDocument && (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-[#003d66]/35 px-4 py-6 backdrop-blur-[2px]" role="dialog" aria-modal="true" aria-label={legalDocument.title}>
        <div className="flex h-[min(90vh,52rem)] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-[0_18px_55px_rgba(0,61,102,0.24)]">
          <div className="flex items-center justify-between border-b border-[#e3eaf0] px-5 py-3">
            <h2 className="m-0 truncate pr-4 text-sm font-semibold text-[#003d66]">{legalDocument.title}</h2>
            <button type="button" onClick={() => setLegalDocument(null)} className="cursor-pointer rounded-md px-3 py-1.5 text-xs font-semibold text-[#526b7b] hover:bg-[#eef8fd]">{t('legal.close')}</button>
          </div>
          <iframe title={legalDocument.title} src={`${legalDocument.url}#toolbar=0`} className="min-h-0 flex-1 border-0" />
        </div>
      </div>
    )}
  </div>
}
